import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { CATEGORIES, STOCK_NOTICE, HERO_VIDEO_URL, mediaUrl } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Sparkles, Truck, ShieldCheck, Baby, ArrowRight } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [catImages, setCatImages] = useState({});

  useEffect(() => {
    api.get("/products").then((r) => {
      const all = r.data;
      setFeatured(all.filter((p) => p.featured).slice(0, 8));
      const map = {};
      for (const p of all) {
        if (!map[p.category] && p.images?.[0]) map[p.category] = p.images[0];
      }
      setCatImages(map);
    });
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-fuchsia-200/40 blur-3xl" />
          <div className="absolute top-40 -right-24 w-96 h-96 rounded-full bg-sky-200/40 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-6 pt-14 pb-10 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-purple-100 text-sm font-medium text-purple-700 shadow-sm">
              <Sparkles className="w-4 h-4 text-fuchsia-500" /> New Season Collection
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-purple-950 leading-[1.05]">
              Adorable styles for your{" "}
              <span className="brand-gradient-text">little stars</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-gray-600 max-w-lg leading-relaxed">
              Curated premium clothing from newborn babies up to 12 years. Crafted for play, comfort and unforgettable memories.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full brand-gradient text-white font-semibold hover:opacity-90 transition shadow-lg shadow-fuchsia-200"
              >
                Shop the Collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/shop/babies"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-purple-200 text-purple-800 font-semibold hover:bg-purple-50 transition"
              >
                <Baby className="w-4 h-4" /> Babies 0-24m
              </Link>
            </div>
            <p className="mt-5 text-sm font-semibold text-emerald-600">🦋 {STOCK_NOTICE}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div
              data-testid="hero-video-iframe"
              className="rounded-3xl overflow-hidden border-2 border-purple-200/60 shadow-2xl aspect-video bg-purple-950 relative"
            >
              <video
                data-testid="hero-video"
                className="w-full h-full object-cover"
                src={HERO_VIDEO_URL}
                autoPlay
                muted
                loop
                playsInline
                controls
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-purple-100 hidden sm:block">
              <p className="font-script text-2xl text-fuchsia-600 leading-none">Ciara's Kidz</p>
              <p className="text-xs text-muted-foreground mt-1">New season 2026 🦋</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Truck, title: "Nationwide Delivery", sub: "Across South Africa" },
            { icon: ShieldCheck, title: "Premium Quality", sub: "Soft, safe fabrics" },
            { icon: Sparkles, title: "0-24m to 12 Years", sub: "Every little size" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3 bg-white rounded-2xl border border-purple-100 p-5">
              <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center">
                <f.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-sm">{f.title}</p>
                <p className="text-xs text-muted-foreground">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-6 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-purple-950">Shop by Age</h2>
            <p className="text-muted-foreground mt-2">Find the perfect fit for every little one.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/shop/${c.key}`}
              data-testid={`home-category-${c.key}`}
              className="card-lift group relative rounded-2xl overflow-hidden aspect-[3/4] border border-purple-100"
            >
              <img src={mediaUrl(catImages[c.key])} alt={c.label} className="absolute inset-0 w-full h-full object-contain p-6 bg-gradient-to-br from-purple-100 to-fuchsia-100 group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-950/80 via-purple-950/10 to-transparent" />
              <div className="absolute bottom-0 p-4 text-white">
                <p className="font-display font-semibold text-lg">{c.label}</p>
                <p className="text-xs text-purple-100">{c.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-6 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-purple-950">Featured Favourites</h2>
            <p className="text-muted-foreground mt-2">Handpicked pieces our little customers love.</p>
          </div>
          <Link to="/shop" className="text-fuchsia-600 font-semibold hover:underline hidden sm:inline-flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Banner */}
      <section className="mx-auto max-w-7xl px-6 mt-20">
        <div className="brand-gradient rounded-3xl p-10 sm:p-14 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <h3 className="font-display text-2xl sm:text-4xl font-bold relative">Create your account today</h3>
          <p className="mt-3 text-white/90 max-w-xl mx-auto relative">
            Track your orders, view your history and be first to shop the new collection dropping 1st October 2026.
          </p>
          <Link
            to="/register"
            className="mt-7 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-purple-800 font-semibold hover:bg-purple-50 transition relative"
          >
            Join Ciara's Kidz <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
